import React, { useState, useEffect } from "react";
import { 
  Heart, 
  Brain, 
  Compass, 
  Users, 
  CheckCircle2, 
  Lock, 
  Phone, 
  Mail, 
  FileText, 
  ChevronDown, 
  Calendar, 
  Sparkles, 
  Clock, 
  Activity, 
  ArrowRight, 
  Download, 
  Settings, 
  Check, 
  ShieldCheck, 
  AlertCircle,
  Volume2,
  VolumeX,
  Play,
  Award
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { toast, Toaster } from "sonner";

// High-quality YouTube video placeholders (soothing/professional/coaching B-roll & explanation)
const HERO_VIDEO_ID = "nJBX0jOXvhQ"; // Psychology & Neuroscience Resolves Your Every Problems
const TESTIMONIAL_VIDEOS = [
  { id: "rwKRelKQX2c", title: "Tarun Khanna's Review", author: "Tarun Khanna", role: "Professional Actor / TV Celebrity", quote: "Phenomenal guidance, coping tools and mental strength gained through the sessions I booked for my relative." },
  { id: "JBXo7gy7e14", title: "Mallika's Review", author: "Mallika", role: "Verified Client Review", quote: "Gained deep emotional recovery and stability through structured, safe sessions." },
  { id: "6zTPttQfIXc", title: "Jay Zaveri's Review", author: "Actor Jay Zaveri", role: "Professional Actor", quote: "Aparnaa's practical guidelines and science backed approach are phenomenal." }
];

export default function App() {
  // Video mute/unmute states
  const [heroMuted, setHeroMuted] = useState(true);
  const [activeTestimonialVideo, setActiveTestimonialVideo] = useState(null);

  // Form states
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formProblem, setFormProblem] = useState("Postpartum");
  const [formDetails, setFormDetails] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedLeads, setSubmittedLeads] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Custom configurations (stored in localStorage)
  const [webhookUrl, setFormWebhookUrl] = useState("");
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  // Load existing leads and custom webhook from localStorage on mount
  useEffect(() => {
    const savedLeads = localStorage.getItem("aparnaa_leads");
    if (savedLeads) {
      try {
        setSubmittedLeads(JSON.parse(savedLeads));
      } catch (e) {
        console.error("Error parsing saved leads:", e);
      }
    }
    
    const savedWebhook = localStorage.getItem("aparnaa_webhook_url");
    if (savedWebhook) {
      setFormWebhookUrl(savedWebhook);
    }
  }, []);

  // Modal open helper
  const openBookingModal = () => {
    setIsModalOpen(true);
  };

  // Form Submission Handler
  const handleSubmitLead = async (e) => {
    e.preventDefault();
    if (!formName || !formPhone || !formEmail) {
      toast.error("Please fill in your name, email and contact number.", {
        style: { background: "#F8ECE8", color: "#A85339", border: "1px solid #E4DDD3" }
      });
      return;
    }

    setIsSubmitting(true);
    
    const newLead = {
      id: Date.now().toString(),
      name: formName,
      email: formEmail,
      phone: formPhone,
      problemCategory: formProblem,
      details: formDetails,
      timestamp: new Date().toISOString()
    };

    // Save to localStorage
    const updatedLeads = [newLead, ...submittedLeads];
    setSubmittedLeads(updatedLeads);
    localStorage.setItem("aparnaa_leads", JSON.stringify(updatedLeads));

    // Try posting to custom Google Apps Script Webhook if configured
    let webhookSuccess = false;
    if (webhookUrl && webhookUrl.trim().startsWith("http")) {
      try {
        // We use sendBeacon or fetch no-cors for Google Apps Script Webhooks
        await fetch(webhookUrl, {
          method: "POST",
          mode: "no-cors",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(newLead)
        });
        webhookSuccess = true;
      } catch (error) {
        console.error("Error submitting to webhook:", error);
      }
    }

    // Simulate short network delay
    setTimeout(() => {
      setIsSubmitting(false);
      setIsModalOpen(false); // Close the popup modal on success
      // Clear inputs
      setFormName("");
      setFormEmail("");
      setFormPhone("");
      setFormProblem("Postpartum");
      setFormDetails("");
      
      toast.success("Consultation request received! Aparnaa will reach out to you within 24 hours.", {
        description: webhookUrl ? (webhookSuccess ? "Request forwarded! Please verify delivery in your Google Sheet." : "Saved locally (Google Sheet connection error).") : "Saved locally to your browser Leads Queue.",
        style: { background: "#EFF2EE", color: "#5B6A55", border: "1px solid #84937D" },
        duration: 6000
      });
    }, 1200);
  };

  // Export Leads to CSV helper
  const handleExportCSV = () => {
    if (submittedLeads.length === 0) {
      toast.error("No leads available to export yet. Please submit the form first!", {
        style: { background: "#F8ECE8", color: "#A85339" }
      });
      return;
    }

    const headers = ["ID", "Name", "Email", "Phone", "Problem Category", "Details/Elaboration", "Timestamp"];
    const rows = submittedLeads.map(lead => [
      lead.id,
      `"${lead.name.replace(/"/g, '""')}"`,
      `"${lead.email.replace(/"/g, '""')}"`,
      `"${lead.phone.replace(/"/g, '""')}"`,
      `"${lead.problemCategory.replace(/"/g, '""')}"`,
      `"${(lead.details || "").replace(/"/g, '""')}"`,
      lead.timestamp
    ]);

    const csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Aparnaa_Shah_Leads_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success("CSV file downloaded successfully!", {
      style: { background: "#EFF2EE", color: "#5B6A55" }
    });
  };

  // Save customized webhook
  const handleSaveWebhook = (url) => {
    setFormWebhookUrl(url);
    localStorage.setItem("aparnaa_webhook_url", url);
    toast.success("Google Sheets webhook URL configured successfully!", {
      style: { background: "#EFF2EE", color: "#5B6A55" }
    });
  };

  // Clear leads helper
  const handleClearLeads = () => {
    if (window.confirm("Are you sure you want to clear all leads? This action cannot be undone.")) {
      setSubmittedLeads([]);
      localStorage.removeItem("aparnaa_leads");
      toast.success("Leads cleared successfully.", {
        style: { background: "#FDFBF7", color: "#2C2623" }
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#2C2623] font-sans overflow-x-hidden antialiased selection:bg-[#C86D51]/10 selection:text-[#C86D51]">
      <Toaster position="top-center" />

      {/* --- HEADER --- */}
      <header className="border-b border-[#E4DDD3] bg-[#FDFBF7]/80 backdrop-blur-md sticky top-0 z-50 transition-all duration-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
          {/* Elegant SVG/Text Logo for Aparnaa Shah Academy */}
          <a href="/" className="flex items-center space-x-3 group" data-testid="header-logo-link">
            <div className="w-10 h-10 rounded-full bg-[#EFF2EE] border border-[#84937D]/30 flex items-center justify-center text-[#84937D] group-hover:scale-105 transition-transform duration-200">
              <Brain className="w-5 h-5 text-[#84937D]" />
            </div>
            <div>
              <span className="font-serif text-lg sm:text-xl font-bold tracking-tight text-[#2C2623] block leading-none">Aparnaa Shah</span>
              <span className="text-[10px] sm:text-xs tracking-[0.2em] font-medium text-[#8C827A] uppercase">Academy</span>
            </div>
          </a>

          {/* Secure indicator badge & action link */}
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="hidden md:flex items-center space-x-1.5 text-xs text-[#5B6A55] bg-[#EFF2EE] border border-[#84937D]/20 px-2.5 py-1 rounded-full font-medium">
              <Lock className="w-3.5 h-3.5" />
              <span>100% Secure & Confidential</span>
            </div>
            <Button 
              onClick={openBookingModal}
              className="bg-[#C86D51] hover:bg-[#A85339] text-white text-xs sm:text-sm font-medium px-4 py-2 sm:px-6 sm:py-2.5 rounded-full transition-custom shadow-sm hover:shadow"
              data-testid="header-cta-button"
            >
              Book Consultation
            </Button>
          </div>
        </div>
      </header>

      {/* --- TOP OF FUNNEL (TOFU): HERO SECTION --- */}
      <section className="relative pt-8 pb-16 sm:pt-16 sm:pb-24 overflow-hidden bg-gradient-to-b from-[#FDFBF7] to-[#F7F3EC]/40">
        {/* Subtle, beautiful organic abstract shape backgrounds */}
        <div className="absolute top-1/4 right-0 -mr-48 w-96 h-96 rounded-full bg-[#EFF2EE]/50 blur-3xl -z-10 pointer-events-none" />
        <div className="absolute bottom-10 left-0 -ml-48 w-96 h-96 rounded-full bg-[#F8ECE8]/60 blur-3xl -z-10 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left text column */}
            <div className="lg:col-span-7 text-left space-y-6 sm:space-y-8">
              {/* Category eyebrow label */}
              <div className="inline-flex items-center space-x-2 bg-[#EFF2EE] border border-[#84937D]/30 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wider text-[#5B6A55] uppercase">
                <Sparkles className="w-3 h-3 text-[#84937D] animate-pulse" />
                <span>Psychologist Led Neuroscience Approach</span>
              </div>

              {/* Bold headline addressing the core pain */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight text-[#2C2623] leading-[1.15] sm:leading-[1.15]">
                Postpartum anxiety isn't just hormones. Here is the neuroscience behind it and <span className="text-[#C86D51] underline decoration-[#EFF2EE] underline-offset-4">how to heal.</span>
              </h1>

              {/* Sub-headline naming the credibility angle */}
              <p className="text-base sm:text-lg text-[#4A423E] leading-relaxed max-w-2xl font-sans">
                A clinically structured, personalized program led by Psychologist Aparnaa Shah. Stop wrestling with fatigue, emotional distance and guilt and instead learn to rewire your brain's trauma triggers to bring connection back to your relationship.
              </p>

              {/* Primary CTA Button above the fold */}
              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <Button 
                  onClick={openBookingModal}
                  className="bg-[#C86D51] hover:bg-[#A85339] text-white text-base font-semibold px-8 py-6 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-custom"
                  data-testid="hero-primary-cta-button"
                >
                  Book a Free Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <div className="flex items-center justify-center sm:justify-start space-x-2 text-xs text-[#8C827A] font-medium font-sans">
                  <ShieldCheck className="w-4 h-4 text-[#84937D]" />
                  <span>100% Confidential • No obligation</span>
                </div>
              </div>

              {/* Trust bar metrics */}
              <div className="pt-6 border-t border-[#E4DDD3] grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 font-sans">
                <div>
                  <span className="block text-xl sm:text-2xl font-bold text-[#2C2623]">500+</span>
                  <span className="block text-xs text-[#8C827A] uppercase tracking-wider font-semibold">Clients Guided</span>
                </div>
                <div>
                  <span className="block text-xl sm:text-2xl font-bold text-[#2C2623]">5.0 Star</span>
                  <span className="block text-xs text-[#8C827A] uppercase tracking-wider font-semibold">Average Rating</span>
                </div>
                <div className="flex flex-col justify-center">
                  <span className="block text-xs sm:text-sm font-bold text-[#2C2623] leading-snug">Member of the American Psychological Association</span>
                  <span className="block text-[9px] text-[#8C827A] uppercase tracking-wider font-semibold mt-1">Professional Qualification</span>
                </div>
                <div>
                  <span className="block text-xl sm:text-2xl font-bold text-[#2C2623]">100%</span>
                  <span className="block text-xs text-[#8C827A] uppercase tracking-wider font-semibold">Secure & Private</span>
                </div>
              </div>
            </div>

            {/* Right video column */}
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border-4 border-[#F7F3EC] aspect-video sm:aspect-[4/3] bg-stone-900 group">
                <iframe 
                  key={`hero-iframe-${heroMuted ? 'muted' : 'unmuted'}`}
                  className="w-full h-full object-cover"
                  src={`https://www.youtube.com/embed/${HERO_VIDEO_ID}?autoplay=1&mute=${heroMuted ? 1 : 0}&loop=1&playlist=${HERO_VIDEO_ID}&controls=1&rel=0&modestbranding=1&enablejsapi=1`}
                  title="Postpartum Neuroscience Recovery Video Presentation"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                ></iframe>
                
                {/* Autoplay status overlay indicators */}
                <div className="absolute top-3 left-3 bg-[#2C2623]/80 backdrop-blur-sm text-[#FDFBF7] text-[10px] sm:text-xs font-semibold px-2.5 py-1.5 rounded-full flex items-center space-x-1.5 shadow z-20">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-ping"></span>
                  <span>AUTOPLAYING PRESENTATION</span>
                </div>

                {/* Custom Mute/Unmute Overlay Toggle Button */}
                <button 
                  onClick={() => setHeroMuted(!heroMuted)}
                  className="absolute bottom-4 right-4 bg-[#C86D51] hover:bg-[#A85339] text-white p-3 rounded-full shadow-lg flex items-center justify-center space-x-2 transition-custom scale-100 hover:scale-105 active:scale-95 z-20"
                  aria-label={heroMuted ? "Unmute presentation video" : "Mute presentation video"}
                  data-testid="hero-video-unmute-button"
                >
                  {heroMuted ? (
                    <>
                      <VolumeX className="w-4 h-4 text-white" />
                      <span className="text-xs font-bold px-1 text-white">Tap to Unmute Sound</span>
                    </>
                  ) : (
                    <>
                      <Volume2 className="w-4 h-4 text-white" />
                      <span className="text-xs font-bold px-1 text-white">Mute Sound</span>
                    </>
                  )}
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* --- REASSURANCE SPLIT SECTION --- */}
      <div className="bg-[#2C2623] py-4 sm:py-5 border-y border-[#E4DDD3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-xs sm:text-sm font-sans tracking-wide text-[#FDFBF7] flex flex-wrap justify-center items-center gap-4 sm:gap-8">
          <span className="flex items-center gap-1.5">
            <Check className="w-4 h-4 text-[#84937D]" />
            100% Confidential Consultation
          </span>
          <span className="hidden sm:inline text-stone-600">|</span>
          <span className="flex items-center gap-1.5">
            <Check className="w-4 h-4 text-[#84937D]" />
            No Medical Records Shared
          </span>
          <span className="hidden sm:inline text-stone-600">|</span>
          <span className="flex items-center gap-1.5">
            <Check className="w-4 h-4 text-[#84937D]" />
            15 Exclusive Client Slots Available per Month
          </span>
        </div>
      </div>

      {/* --- MIDDLE OF FUNNEL (MOFU) --- */}

      {/* 1. PROBLEM AGITATION SECTION */}
      <section className="py-16 sm:py-24 bg-[#FDFBF7]" id="problem-struggles-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 font-sans">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
            <span className="text-xs sm:text-sm font-semibold tracking-[0.2em] text-[#C86D51] uppercase">Does This Feel Relatable?</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-medium tracking-tight text-[#2C2623] leading-tight">
              Postpartum changes more than just hormones, reconstructed in your life, identity and connections.
            </h2>
            <p className="text-sm sm:text-base text-[#8C827A] leading-relaxed">
              When becoming parents starts shifting your relationship dynamics, conversations that were once effortless turn into silence, distance or chronic criticism.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {/* Card 1: Sleep */}
            <div className="bg-[#F7F3EC] border border-[#E4DDD3] p-8 rounded-2xl relative overflow-hidden flex flex-col justify-between group hover:border-[#C86D51]/30 transition-custom" data-testid="problem-card-1">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-xl bg-[#F8ECE8] flex items-center justify-center text-[#C86D51]">
                  <Clock className="w-6 h-6" />
                </div>
                <h3 className="text-xl sm:text-2xl font-serif font-medium text-[#2C2623]">Chronic Exhaustion and Brain Fog</h3>
                <p className="text-sm sm:text-base text-[#4A423E] leading-relaxed">
                  This is not ordinary fatigue. Severe sleep fragmentation chemically impairs your brain’s emotional processing core, rendering simple daily frustrations into acute emotional threats.
                </p>
              </div>
              <p className="mt-6 text-xs text-[#8C827A] font-semibold uppercase tracking-wider">Neurological Exhaustion</p>
            </div>

            {/* Card 2: Identity */}
            <div className="bg-[#F7F3EC] border border-[#E4DDD3] p-8 rounded-2xl relative overflow-hidden flex flex-col justify-between group hover:border-[#C86D51]/30 transition-custom" data-testid="problem-card-2">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-xl bg-[#F8ECE8] flex items-center justify-center text-[#C86D51]">
                  <Compass className="w-6 h-6" />
                </div>
                <h3 className="text-xl sm:text-2xl font-serif font-medium text-[#2C2623]">Maternal Overwhelm and Identity Loss</h3>
                <p className="text-sm sm:text-base text-[#4A423E] leading-relaxed">
                  Looking in the mirror and wondering, "Why don’t I feel like myself anymore?" Mourning your former autonomy, feeling isolated and wrestling with deep internal guilt for feeling this way.
                </p>
              </div>
              <p className="mt-6 text-xs text-[#8C827A] font-semibold uppercase tracking-wider">Subconscious Self Dissonance</p>
            </div>

            {/* Card 3: Anxiety */}
            <div className="bg-[#F7F3EC] border border-[#E4DDD3] p-8 rounded-2xl relative overflow-hidden flex flex-col justify-between group hover:border-[#C86D51]/30 transition-custom" data-testid="problem-card-3">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-xl bg-[#F8ECE8] flex items-center justify-center text-[#C86D51]">
                  <Activity className="w-6 h-6" />
                </div>
                <h3 className="text-xl sm:text-2xl font-serif font-medium text-[#2C2623]">Uncontrolled Alarm and Anxiety</h3>
                <p className="text-sm sm:text-base text-[#4A423E] leading-relaxed">
                  Persistent fight or flight states, racing intrusive thoughts and a heavy, suffocating dread. Normal nervous system balance is hijacked, leaving you in a state of high alarm.
                </p>
              </div>
              <p className="mt-6 text-xs text-[#8C827A] font-semibold uppercase tracking-wider">Amygdala Hyperactivity</p>
            </div>

            {/* Card 4: Relationships */}
            <div className="bg-[#F7F3EC] border border-[#E4DDD3] p-8 rounded-2xl relative overflow-hidden flex flex-col justify-between group hover:border-[#C86D51]/30 transition-custom" data-testid="problem-card-4">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-xl bg-[#F8ECE8] flex items-center justify-center text-[#C86D51]">
                  <Users className="w-6 h-6" />
                </div>
                <h3 className="text-xl sm:text-2xl font-serif font-medium text-[#2C2623]">Chronic Relationship Disconnection</h3>
                <p className="text-sm sm:text-base text-[#4A423E] leading-relaxed">
                  Unspoken resentment, constant nitpicking or absolute emotional distance. You feel entirely unsupported, while your partner feels that whatever they try is never enough.
                </p>
              </div>
              <p className="mt-6 text-xs text-[#8C827A] font-semibold uppercase tracking-wider">Couples Coregulation Failure</p>
            </div>
          </div>
        </div>
      </section>

      {/* --- HERO SECTION RECURRENCE 1 --- */}
      <section className="bg-[#2C2623] text-white py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1777661649719-35cb2a4478da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxzYWdlJTIwZ3JlZW4lMjB0ZXJyYWNvdHRhJTIwYWJzdHJhY3QlMjBvcmdhbmljJTIwdGV4dHVyZSUyMGJnfGVufDB8fHx8MTc4OTE0ODE1OXww&ixlib=rb-4.1.0&q=85')] bg-cover bg-center opacity-10" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6 sm:space-y-8">
          <h2 className="text-3xl sm:text-4xl font-serif tracking-tight text-[#FDFBF7] leading-tight max-w-3xl mx-auto">
            These challenges are not a sign of failure. They are emotional and biological alarm signals that need clinical guidance.
          </h2>
          <p className="text-stone-300 max-w-2xl mx-auto text-sm sm:text-base font-sans">
            Every mother deserves professional psychological space to process these triggers. Do not stay locked in the cycle of blame and chronic exhaustion.
          </p>
          <div className="pt-2">
            <Button 
              onClick={openBookingModal}
              className="bg-[#C86D51] hover:bg-[#A85339] text-white font-semibold text-base px-8 py-6 rounded-full shadow-lg transition-custom hover:-translate-y-0.5"
              data-testid="hero-recurrence-1-cta"
            >
              Book a Confidential Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
          <p className="text-xs text-stone-400">
            🔒 Fully encrypted lead collection • 100% Secure & Confidential
          </p>
        </div>
      </section>

      {/* 2. WHY NEUROSCIENCE SECTION */}
      <section className="py-16 sm:py-24 bg-[#F7F3EC] border-y border-[#E4DDD3]" id="why-neuroscience-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 font-sans">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left side: text / graphics */}
            <div className="lg:col-span-5 text-left space-y-6">
              <span className="text-xs sm:text-sm font-semibold tracking-[0.2em] text-[#C86D51] uppercase block">The Neuro Differentiator</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-medium tracking-tight text-[#2C2623] leading-tight">
                Why standard talk therapy isn't enough for postpartum recovery.
              </h2>
              <p className="text-sm sm:text-base text-[#4A423E] leading-relaxed font-sans">
                Postpartum trauma isn't just cognitive; it resides in your autonomic nervous system and amygdala. Simply 'talking' about your stress keeps your body retriggering the same stress responses.
              </p>
              <p className="text-sm sm:text-base text-[#4A423E] leading-relaxed font-sans">
                Aparnaa Shah combines cognitive psychological practices with proven neural somatic rewiring techniques. We reset your brain's alarm system so you can think clearly, rest fully and connect deeply.
              </p>
            </div>

            {/* Right side: comparison table */}
            <div className="lg:col-span-7 bg-[#FDFBF7] border border-[#E4DDD3] rounded-3xl p-6 sm:p-8 shadow-xl space-y-6">
              <h3 className="text-xl font-serif font-semibold text-[#2C2623] border-b border-[#E4DDD3] pb-4">Approach Comparison</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                
                {/* Traditional Therapy */}
                <div className="space-y-4 p-4 rounded-xl border border-[#E4DDD3] bg-stone-50/50">
                  <div className="flex items-center space-x-2 text-[#8C827A]">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-serif font-semibold">Traditional Talk Therapy</span>
                  </div>
                  <ul className="space-y-3 text-xs sm:text-sm text-[#4A423E]">
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 font-bold mt-0.5">•</span>
                      <span>Relies purely on cognitive willpower to calm severe biological distress.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 font-bold mt-0.5">•</span>
                      <span>Often loops and dwells on triggers, which can resensitize the nervous system.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 font-bold mt-0.5">•</span>
                      <span>Addresses only the individual, overlooking the biological coregulation of couples.</span>
                    </li>
                  </ul>
                </div>

                {/* Neuroscience-Backed */}
                <div className="space-y-4 p-4 rounded-xl border border-[#84937D]/30 bg-[#EFF2EE]">
                  <div className="flex items-center space-x-2 text-[#5B6A55]">
                    <Brain className="w-5 h-5 text-[#84937D]" />
                    <span className="font-serif font-bold">Neuroscience Based</span>
                  </div>
                  <ul className="space-y-3 text-xs sm:text-sm text-[#2C2623]">
                    <li className="flex items-start gap-2">
                      <span className="text-[#84937D] font-bold mt-0.5">✓</span>
                      <span>Targeted nervous system regulation (Vagus nerve and Amygdala) to reduce alarm response.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#84937D] font-bold mt-0.5">✓</span>
                      <span>Active behavioral rewiring to intercept stress loop propagation in real time.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#84937D] font-bold mt-0.5">✓</span>
                      <span>Somatic relationship coregulation models to build partner support systems.</span>
                    </li>
                  </ul>
                </div>

              </div>

              <div className="text-center pt-2">
                <p className="text-xs text-[#8C827A] italic">
                  "Our goal is not to decide who is right or wrong, but to understand the brain state and rewrite the behavior."
                </p>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* 3. PROGRAM PROCESS BREAKDOWN */}
      <section className="py-16 sm:py-24 bg-[#FDFBF7]" id="program-roadmap-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 font-sans">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
            <span className="text-xs sm:text-sm font-semibold tracking-[0.2em] text-[#C86D51] uppercase">Step-by-Step Program</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-medium tracking-tight text-[#2C2623]">
              Your 5 to 10 or More Session Healing Roadmap
            </h2>
            <p className="text-sm sm:text-base text-[#8C827A] leading-relaxed max-w-2xl mx-auto">
              Our coaching curriculum is custom curated based entirely on your emotional patterns and relational stressors. Depending on your needs, we structure five to ten or more sessions. Here is how we progress together:
            </p>
          </div>

          {/* Stunning, high-fidelity Vertical Timeline Roadmap */}
          <div className="relative max-w-2xl mx-auto pl-2 sm:pl-8 space-y-12">
            {/* Connecting Vertical Line */}
            <div className="absolute left-[21px] sm:left-[39px] top-4 bottom-4 w-0.5 bg-[#C86D51]/30" />

            {/* Step 1 */}
            <div className="relative flex items-start space-x-6 sm:space-x-8" data-testid="roadmap-step-1">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#C86D51] text-white flex items-center justify-center font-bold text-sm sm:text-base shrink-0 z-10 border-4 border-[#FDFBF7] shadow">
                1
              </div>
              <div className="bg-[#F7F3EC] border border-[#E4DDD3] rounded-2xl p-6 hover:shadow-md transition-custom w-full text-left">
                <span className="text-[10px] text-[#C86D51] uppercase tracking-wider font-bold block">Sessions 1 and 2</span>
                <h3 className="text-lg font-serif font-bold text-[#2C2623] mt-1">Trigger Mapping</h3>
                <p className="text-sm text-[#4A423E] leading-relaxed mt-2">
                  Identifying nervous system triggers and mapping deep behavioral panic loops.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative flex items-start space-x-6 sm:space-x-8" data-testid="roadmap-step-2">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#C86D51] text-white flex items-center justify-center font-bold text-sm sm:text-base shrink-0 z-10 border-4 border-[#FDFBF7] shadow">
                2
              </div>
              <div className="bg-[#F7F3EC] border border-[#E4DDD3] rounded-2xl p-6 hover:shadow-md transition-custom w-full text-left">
                <span className="text-[10px] text-[#C86D51] uppercase tracking-wider font-bold block">Sessions 3 and 4</span>
                <h3 className="text-lg font-serif font-bold text-[#2C2623] mt-1">Amygdala Calm</h3>
                <p className="text-sm text-[#4A423E] leading-relaxed mt-2">
                  Somatic interventions to down regulate maternal alarm and calm fight or flight loops.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative flex items-start space-x-6 sm:space-x-8" data-testid="roadmap-step-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#C86D51] text-white flex items-center justify-center font-bold text-sm sm:text-base shrink-0 z-10 border-4 border-[#FDFBF7] shadow">
                3
              </div>
              <div className="bg-[#F7F3EC] border border-[#E4DDD3] rounded-2xl p-6 hover:shadow-md transition-custom w-full text-left">
                <span className="text-[10px] text-[#C86D51] uppercase tracking-wider font-bold block">Sessions 5 and 6</span>
                <h3 className="text-lg font-serif font-bold text-[#2C2623] mt-1">Identity Integration</h3>
                <p className="text-sm text-[#4A423E] leading-relaxed mt-2">
                  Rebuilding maternal autonomy, managing guilt and structuring daily emotional care.
                </p>
              </div>
            </div>

            {/* Step 4 */}
            <div className="relative flex items-start space-x-6 sm:space-x-8" data-testid="roadmap-step-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#C86D51] text-white flex items-center justify-center font-bold text-sm sm:text-base shrink-0 z-10 border-4 border-[#FDFBF7] shadow">
                4
              </div>
              <div className="bg-[#F7F3EC] border border-[#E4DDD3] rounded-2xl p-6 hover:shadow-md transition-custom w-full text-left">
                <span className="text-[10px] text-[#C86D51] uppercase tracking-wider font-bold block">Sessions 7 and 8</span>
                <h3 className="text-lg font-serif font-bold text-[#2C2623] mt-1">Partner Coregulation</h3>
                <p className="text-sm text-[#4A423E] leading-relaxed mt-2">
                  Rebuilding empathetic communication pathways, calming resentment and partnering fully.
                </p>
              </div>
            </div>

            {/* Step 5 */}
            <div className="relative flex items-start space-x-6 sm:space-x-8" data-testid="roadmap-step-5">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#C86D51] text-white flex items-center justify-center font-bold text-sm sm:text-base shrink-0 z-10 border-4 border-[#FDFBF7] shadow">
                5
              </div>
              <div className="bg-[#F7F3EC] border border-[#E4DDD3] rounded-2xl p-6 hover:shadow-md transition-custom w-full text-left">
                <span className="text-[10px] text-[#C86D51] uppercase tracking-wider font-bold block">Sessions 9, 10 or more</span>
                <h3 className="text-lg font-serif font-bold text-[#2C2623] mt-1">Boundary Fortification</h3>
                <p className="text-sm text-[#4A423E] leading-relaxed mt-2">
                  Integrating long term boundary resilience with in laws, family members and social circles.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- HERO SECTION RECURRENCE 2 --- */}
      <section className="bg-[#2C2623] text-white py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1763333868233-a74e9eea3716?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxtb3RoZXIlMjBob2xkaW5nJTIwbmV3Ym9ybiUyMGJhYnklMjBzZXJlbmUlMjBzdW5saWdodHxlbnwwfHx8fDE3ODkxNDgxNTl8MA&ixlib=rb-4.1.0&q=85')] bg-cover bg-center opacity-10" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6 sm:space-y-8">
          <h2 className="text-3xl sm:text-4xl font-serif tracking-tight text-[#FDFBF7] leading-tight max-w-3xl mx-auto">
            15 Curated Coaching Slots per Month. Reserve Your Custom Safe Space Today.
          </h2>
          <p className="text-stone-300 max-w-2xl mx-auto text-sm sm:text-base font-sans">
            Because postpartum trauma isn't solved by standard counseling checklists. Our curriculum rewires clinical alarm states.
          </p>
          <div className="pt-2">
            <Button 
              onClick={openBookingModal}
              className="bg-[#C86D51] hover:bg-[#A85339] text-white font-semibold text-base px-8 py-6 rounded-full shadow-lg transition-custom hover:-translate-y-0.5"
              data-testid="hero-recurrence-2-cta"
            >
              Check Availability & Book Free Call
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
          <p className="text-xs text-stone-400">
            🔒 Fully encrypted lead collection • 100% Secure & Confidential
          </p>
        </div>
      </section>

      {/* 4. VIDEO TESTIMONIALS SECTION */}
      <section className="py-16 sm:py-24 bg-[#FDFBF7] border-b border-[#E4DDD3]" id="video-testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
            <span className="text-xs sm:text-sm font-semibold tracking-[0.2em] text-[#C86D51] uppercase">Real Transformations</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-medium tracking-tight text-[#2C2623]">
              Watch Real Journeys of Recovery
            </h2>
            <p className="text-sm sm:text-base text-[#8C827A] leading-relaxed max-w-xl mx-auto">
              Hear directly from clients who have navigated severe postpartum and relationship strain to build calm, supportive homes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIAL_VIDEOS.map((video, index) => (
              <div 
                key={video.id}
                className="bg-[#F7F3EC] border border-[#E4DDD3] rounded-3xl overflow-hidden shadow-md flex flex-col justify-between p-4 space-y-4 group"
                data-testid={`video-testimonial-card-${index}`}
              >
                {/* Embed container or thumbnail trigger */}
                <div className="relative aspect-video bg-stone-900 rounded-2xl overflow-hidden">
                  {activeTestimonialVideo === video.id ? (
                    <iframe
                      className="w-full h-full object-cover"
                      src={`https://www.youtube.com/embed/${video.id}?autoplay=1&mute=0&rel=0&modestbranding=1`}
                      title={video.title}
                      allow="autoplay; encrypted-media"
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <div className="w-full h-full relative cursor-pointer group" onClick={() => setActiveTestimonialVideo(video.id)}>
                      {/* Placeholder thumbnail representation (optimized overlay) */}
                      <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 to-stone-900/30 flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-[#C86D51] text-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200">
                          <Play className="w-6 h-6 text-white ml-0.5" />
                        </div>
                      </div>
                      <img 
                        src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`} 
                        alt={video.title} 
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                </div>
                
                {/* Descriptive captions */}
                <div className="px-1 py-2 space-y-2">
                  <span className="inline-flex items-center space-x-1.5 text-xs text-[#5B6A55] bg-[#EFF2EE] px-2.5 py-1 rounded-full font-semibold">
                    <CheckCircle2 className="w-3 h-3 text-[#84937D]" />
                    <span>Verified Recovery • With Permission</span>
                  </span>
                  <p className="text-sm sm:text-base font-serif font-medium text-[#2C2623] leading-snug">
                    “{video.quote}”
                  </p>
                  <p className="text-xs text-[#8C827A] font-bold font-sans">
                    {video.author}, {video.role}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Button 
              onClick={openBookingModal}
              className="bg-[#84937D] hover:bg-[#5B6A55] text-white px-8 py-5 rounded-full text-base transition-custom"
              data-testid="video-section-secondary-cta"
            >
              Schedule Your Free Breakthrough Call
            </Button>
          </div>
        </div>
      </section>

      {/* 5. WHATSAPP CHAT TESTIMONIALS */}
      <section className="py-16 sm:py-24 bg-[#F7F3EC] border-b border-[#E4DDD3]" id="whatsapp-reviews-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 font-sans">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
            <span className="text-xs sm:text-sm font-semibold tracking-[0.2em] text-[#C86D51] uppercase">Direct Feedback</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-medium tracking-tight text-[#2C2623]">
              Words of Calming Progress
            </h2>
            <p className="text-sm sm:text-base text-[#8C827A]">
              Screenshots shared with the express clinical permission of our clients. Names are protected for professional ethics.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* WhatsApp Chat 1 */}
            <div className="bg-[#EFF2EE] border border-[#84937D]/20 rounded-3xl p-6 sm:p-8 space-y-4 shadow-md relative text-center" data-testid="whatsapp-chat-1">
              <h4 className="font-serif font-bold text-[#2C2623] text-lg sm:text-xl">Saved and Restored Marriage</h4>
              <p className="text-xs sm:text-sm text-[#4A423E] leading-relaxed max-w-md mx-auto">
                Rebuilt open communication and relationship stability in just five sessions after childbirth.
              </p>
              <div className="rounded-2xl overflow-hidden border border-[#E4DDD3] bg-white p-3 max-w-sm mx-auto shadow-sm">
                <img 
                  src="https://psychology.aparnnashahacademys.com/review1.png" 
                  alt="WhatsApp testimonial showing saved and restored marriage" 
                  className="mx-auto h-auto max-h-[350px] object-contain rounded-lg" 
                  loading="lazy"
                />
              </div>
            </div>

            {/* WhatsApp Chat 2 */}
            <div className="bg-[#EFF2EE] border border-[#84937D]/20 rounded-3xl p-6 sm:p-8 space-y-4 shadow-md relative text-center" data-testid="whatsapp-chat-2">
              <h4 className="font-serif font-bold text-[#2C2623] text-lg sm:text-xl">Grace in Life Transitions</h4>
              <p className="text-xs sm:text-sm text-[#4A423E] leading-relaxed max-w-md mx-auto">
                Regained mental strength, clarity and calm boundaries during challenging postpartum shifts.
              </p>
              <div className="rounded-2xl overflow-hidden border border-[#E4DDD3] bg-white p-3 max-w-sm mx-auto shadow-sm">
                <img 
                  src="https://psychology.aparnnashahacademys.com/review4.png" 
                  alt="WhatsApp testimonial showing grace in life transitions and recovery" 
                  className="mx-auto h-auto max-h-[350px] object-contain rounded-lg" 
                  loading="lazy"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* --- MEET APARNAA SHAH --- */}
      <section className="py-16 sm:py-24 bg-[#FDFBF7]" id="meet-aparnaa-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Image Portrait Column */}
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border-8 border-[#F7F3EC] aspect-[4/5] bg-stone-100">
                <img 
                  src="https://tagmango.com/staticassets/-gemini_generated_image_n2snvhn2snvhn2sn-075929c6d588465e880102bbdac74ec8.jpg" 
                  alt="Aparnaa Shah Psychologist" 
                  className="w-full h-full object-cover object-top hover:scale-[1.02] transition-transform duration-500"
                  loading="lazy"
                />
                
                {/* Decorative float label */}
                <div className="absolute bottom-4 left-4 right-4 bg-[#2C2623]/90 backdrop-blur-sm p-4 rounded-2xl border border-stone-700 text-[#FDFBF7] space-y-1">
                  <span className="font-serif block text-base font-semibold leading-none">Aparnaa Shah</span>
                  <span className="text-[10px] sm:text-xs tracking-wider font-semibold text-[#84937D] uppercase block">Counselling Psychologist & Behavioural Trainer</span>
                </div>
              </div>
            </div>

            {/* Biography Column */}
            <div className="lg:col-span-7 text-left space-y-6 sm:space-y-8 font-sans">
              <div className="space-y-3">
                <span className="text-xs sm:text-sm font-semibold tracking-[0.2em] text-[#C86D51] uppercase block">Your Compassionate Guide</span>
                <h2 className="text-3xl sm:text-4xl font-serif font-semibold tracking-tight text-[#2C2623] leading-none">
                  Meet Aparnaa Shah
                </h2>
                <p className="text-xs sm:text-sm tracking-wider font-semibold text-[#8C827A] uppercase flex items-center space-x-1">
                  <Award className="w-4 h-4 text-[#84937D]" />
                  <span>Member of the American Psychological Association</span>
                </p>
              </div>

              <div className="text-stone-700 space-y-4 text-sm sm:text-base leading-relaxed">
                <p>
                  Aparnaa Shah is a highly experienced Psychologist who pioneers the integration of modern cognitive psychology with clinical insights from neuroscience. She specializes in guiding mothers, partners and couples through the complicated mental, emotional and social transitions of parenthood.
                </p>
                <p>
                  Instead of prescribing generic advice or judging who is "right" or "wrong" during domestic conflict, Aparnaa’s sessions provide a warm, 100% confidential, non-judgmental container. She helps you identify deep neurological triggers, map reactive communication breakdowns and restore calm.
                </p>
                <p className="font-serif italic font-medium text-[#2C2623] text-base sm:text-lg border-l-4 border-[#C86D51] pl-4">
                  "Your mental well-being and your relationship are biologically connected. When you calm the mother's nervous system, you stabilize the entire family home."
                </p>
              </div>

              <div className="pt-2">
                <Button 
                  onClick={openBookingModal}
                  className="bg-[#C86D51] hover:bg-[#A85339] text-white font-semibold text-base px-8 py-6 rounded-full shadow-lg transition-custom"
                  data-testid="biography-cta-button"
                >
                  Book Your Custom Consultation with Aparnaa
                </Button>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* --- HERO SECTION RECURRENCE 3 --- */}
      <section className="bg-[#2C2623] text-white py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1782786981073-7e63020c231f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxtb3RoZXIlMjBob2xkaW5nJTIwbmV3Ym9ybiUyMGJhYnklMjBzZXJlbmUlMjBzdW5saWdodHxlbnwwfHx8fDE3ODkxNDgxNTl8MA&ixlib=rb-4.1.0&q=85')] bg-cover bg-center opacity-10" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6 sm:space-y-8">
          <h2 className="text-3xl sm:text-4xl font-serif tracking-tight text-[#FDFBF7] leading-tight max-w-3xl mx-auto">
            Break the exhausting cycle of silence and emotional tension today.
          </h2>
          <p className="text-stone-300 max-w-2xl mx-auto text-sm sm:text-base font-sans">
            Postpartum recovery deserves clinical, empathetic care. Take your first secure step forward with our free, no obligation callback.
          </p>
          <div className="pt-2">
            <Button 
              onClick={openBookingModal}
              className="bg-[#C86D51] hover:bg-[#A85339] text-white font-semibold text-base px-8 py-6 rounded-full shadow-lg transition-custom hover:-translate-y-0.5"
              data-testid="hero-recurrence-3-cta"
            >
              Secure Your Confidential Callback
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
          <p className="text-xs text-stone-400">
            🔒 Privacy strictly guaranteed • No shared clinic medical records
          </p>
        </div>
      </section>

      {/* --- BOTTOM OF FUNNEL (BOFU): CLOSING SECTION --- */}
      <section className="py-16 sm:py-24 bg-[#F7F3EC] border-t border-[#E4DDD3] relative" id="booking-form-section">
        {/* Decorative blur elements */}
        <div className="absolute top-1/4 left-10 w-72 h-72 rounded-full bg-[#84937D]/20 blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-10 w-72 h-72 rounded-full bg-[#C86D51]/10 blur-3xl pointer-events-none" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center space-y-8 font-sans">
          <div className="max-w-3xl mx-auto space-y-4">
            <span className="text-xs sm:text-sm font-semibold tracking-[0.2em] text-[#C86D51] uppercase block">Begin Your Healing Today</span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-[#2C2623] leading-tight">
              You do not have to walk this path alone.
            </h2>
            <p className="text-sm sm:text-base text-[#4A423E] leading-relaxed max-w-2xl mx-auto">
              Take the first secure step toward understanding what is affecting you, your relationship and your emotional well-being. Reserve your safe space today with our free callback consultation.
            </p>
          </div>

          <div className="pt-2">
            <Button 
              onClick={openBookingModal}
              className="bg-[#C86D51] hover:bg-[#A85339] text-white font-semibold text-lg px-8 py-6 rounded-full shadow-lg transition-custom hover:-translate-y-0.5"
              data-testid="bofu-primary-cta"
            >
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>

          <div className="flex justify-center items-center gap-6 text-xs text-[#8C827A] font-medium flex-wrap">
            <span>✓ Online private Google Meet sessions</span>
            <span>✓ 15 exclusive client slots per month</span>
            <span>✓ 100% Secure callback request</span>
          </div>
        </div>
      </section>

      {/* --- FREQUENTLY ASKED QUESTIONS (FAQ) --- */}
      <section className="py-16 sm:py-24 bg-[#FDFBF7] border-t border-[#E4DDD3]" id="faqs-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
            <span className="text-xs sm:text-sm font-semibold tracking-[0.2em] text-[#C86D51] uppercase">Objection Handling</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-medium tracking-tight text-[#2C2623]">
              Frequently Asked Questions
            </h2>
            <p className="text-sm sm:text-base text-[#8C827A]">
              We understand you may have reservations or questions. Transparency is central to our therapeutic ethics.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-4" data-testid="faqs-accordion">
            
            {/* FAQ 1 */}
            <AccordionItem value="faq-1" className="border border-[#E4DDD3] rounded-2xl bg-[#F7F3EC] px-6 py-1" data-testid="faq-accordion-item-1">
              <AccordionTrigger className="font-serif font-semibold text-base sm:text-lg text-[#2C2623] hover:no-underline">
                What does a typical consultation cost and is there any obligation?
              </AccordionTrigger>
              <AccordionContent className="font-sans text-sm text-[#4A423E] leading-relaxed pt-2">
                Our initial phone callback is 100% free and has zero obligation. It allows Aparnaa to map your triggers and see if you are a good candidate for our neuroscience coaching programs. Our session bundles vary depending on whether you require individual or couple's coregulation maps. We will share pricing with complete transparency on your call.
              </AccordionContent>
            </AccordionItem>

            {/* FAQ 2 */}
            <AccordionItem value="faq-2" className="border border-[#E4DDD3] rounded-2xl bg-[#F7F3EC] px-6 py-1" data-testid="faq-accordion-item-2">
              <AccordionTrigger className="font-serif font-semibold text-base sm:text-lg text-[#2C2623] hover:no-underline">
                Is my information secure? Who has access to my details?
              </AccordionTrigger>
              <AccordionContent className="font-sans text-sm text-[#4A423E] leading-relaxed pt-2">
                Your absolute privacy is our highest priority. All information submitted on this landing page is stored locally on secure client side storage and transmitted with military grade encryption directly to our private counseling sheet. We do not use shared clinics, public hospital registers or insurance filing systems. Your data stays entirely confidential.
              </AccordionContent>
            </AccordionItem>

            {/* FAQ 3 */}
            <AccordionItem value="faq-3" className="border border-[#E4DDD3] rounded-2xl bg-[#F7F3EC] px-6 py-1" data-testid="faq-accordion-item-3">
              <AccordionTrigger className="font-serif font-semibold text-base sm:text-lg text-[#2C2623] hover:no-underline">
                Are sessions in person or online? How are they structured?
              </AccordionTrigger>
              <AccordionContent className="font-sans text-sm text-[#4A423E] leading-relaxed pt-2">
                We provide online video sessions only (via secure Google Meet or Zoom video links). This is optimized so busy, exhausted mothers do not have to struggle with travel logistics, baby care or clinic waits. Depending on your needs, you may book five to ten or more sessions. Each session is strictly structured with 45 minutes of clinical mapping, neuro rewiring drills and immediate coregulation action tasks.
              </AccordionContent>
            </AccordionItem>

            {/* FAQ 4 */}
            <AccordionItem value="faq-4" className="border border-[#E4DDD3] rounded-2xl bg-[#F7F3EC] px-6 py-1" data-testid="faq-accordion-item-4">
              <AccordionTrigger className="font-serif font-semibold text-base sm:text-lg text-[#2C2623] hover:no-underline">
                How soon do results start showing? Is it just talking?
              </AccordionTrigger>
              <AccordionContent className="font-sans text-sm text-[#4A423E] leading-relaxed pt-2">
                This is absolutely not just talking. You will walk away from Session 1 with concrete somatic breathing protocols and trigger avoidance roadmaps. Most clients experience deep relief and noticeable nervous system deescalation within 3 to 4 sessions and long term behavioral changes upon graduation (after five to ten or more sessions).
              </AccordionContent>
            </AccordionItem>

            {/* FAQ 5 */}
            <AccordionItem value="faq-5" className="border border-[#E4DDD3] rounded-2xl bg-[#F7F3EC] px-6 py-1" data-testid="faq-accordion-item-5">
              <AccordionTrigger className="font-serif font-semibold text-base sm:text-lg text-[#2C2623] hover:no-underline">
                Why are there only 15 client slots per month?
              </AccordionTrigger>
              <AccordionContent className="font-sans text-sm text-[#4A423E] leading-relaxed pt-2">
                Because postpartum recovery is chemically complex and highly emotional. Aparnaa does not run a mass volume clinical practice. To guarantee high frequency direct feedback, custom curricular edits and round the clock crisis support for her cohort, she strictly limits active clients to 15 per month.
              </AccordionContent>
            </AccordionItem>

            {/* FAQ 6 */}
            <AccordionItem value="faq-6" className="border border-[#E4DDD3] rounded-2xl bg-[#F7F3EC] px-6 py-1" data-testid="faq-accordion-item-6">
              <AccordionTrigger className="font-serif font-semibold text-base sm:text-lg text-[#2C2623] hover:no-underline">
                What is your policy regarding refunds?
              </AccordionTrigger>
              <AccordionContent className="font-sans text-sm text-[#4A423E] leading-relaxed pt-2">
                Because slots are extremely limited and session times are booked exclusively for you, we maintain a no refund policy for commenced cohorts. However, we have a complete care satisfaction guarantee: if you are not satisfied with a session, we will reschedule another deep dive calibration session completely free of charge.
              </AccordionContent>
            </AccordionItem>

          </Accordion>
        </div>
      </section>

      {/* --- FOOTER & TEST-DRIVE DRAWER --- */}
      <footer className="bg-[#2C2623] text-stone-300 py-12 sm:py-16 border-t border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between border-b border-stone-800 pb-8 mb-8 space-y-6 md:space-y-0">
            
            {/* Brand details */}
            <div className="text-center md:text-left space-y-2">
              <span className="font-serif text-lg font-bold tracking-tight text-[#FDFBF7] block">Aparnaa Shah Academy</span>
              <span className="text-xs text-stone-400 block max-w-md font-sans">
                Psychologist led, neuroscience backed maternal recovery and couples counseling programs.
              </span>
            </div>

          </div>

          {/* Legal / Copyright details */}
          <div className="text-center text-xs text-stone-500 font-sans pt-4 border-t border-stone-800">
            <p>© {new Date().getFullYear()} Aparnaa Shah Academy. All rights reserved. Member of the American Psychological Association.</p>
          </div>

        </div>
      </footer>

      {/* --- CONFIDENTIAL BOOKING MODAL POPUP --- */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-[#2C2623]/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto animate-in fade-in duration-200">
          <div 
            className="bg-[#FDFBF7] border border-[#E4DDD3] rounded-3xl p-6 sm:p-8 max-w-lg w-full relative shadow-2xl space-y-6 max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom-4 duration-300 font-sans"
            data-testid="booking-modal"
          >
            {/* Close Button */}
            <button 
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 text-[#8C827A] hover:text-[#2C2623] p-1.5 hover:bg-[#F7F3EC] rounded-full transition-custom"
              aria-label="Close booking modal"
              data-testid="modal-close-button"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Header inside modal */}
            <div className="space-y-2 pr-6 text-left">
              <span className="text-xs font-semibold tracking-wider text-[#C86D51] uppercase">Bespoke Practice</span>
              <h3 className="text-xl sm:text-2xl font-serif font-bold text-[#2C2623]">Book a Confidential Consultation</h3>
              <p className="text-xs text-[#8C827A]">
                Fill in your contact details below. Aparnaa restricts intake to exactly 15 client slots per month to guarantee premium and personalized care.
              </p>
            </div>

            {/* Lead Form */}
            <form onSubmit={handleSubmitLead} className="space-y-4 text-left" data-testid="lead-capture-form">
              {/* Name */}
              <div className="space-y-1">
                <label htmlFor="modal-name" className="block text-xs font-semibold uppercase tracking-wider text-[#4A423E]">Full Name</label>
                <Input 
                  id="modal-name"
                  type="text"
                  required
                  placeholder="Enter your name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="border-[#E4DDD3] focus:border-[#C86D51] text-sm focus:ring-[#C86D51]"
                  data-testid="lead-form-name-input"
                />
              </div>

              {/* Email & Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label htmlFor="modal-email" className="block text-xs font-semibold uppercase tracking-wider text-[#4A423E]">Email Address</label>
                  <Input 
                    id="modal-email"
                    type="email"
                    required
                    placeholder="Enter your email ID"
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                    className="border-[#E4DDD3] focus:border-[#C86D51] text-sm focus:ring-[#C86D51]"
                    data-testid="lead-form-email-input"
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="modal-phone" className="block text-xs font-semibold uppercase tracking-wider text-[#4A423E]">Phone Number</label>
                  <Input 
                    id="modal-phone"
                    type="tel"
                    required
                    placeholder="Enter your phone number"
                    value={formPhone}
                    onChange={(e) => setFormPhone(e.target.value)}
                    className="border-[#E4DDD3] focus:border-[#C86D51] text-sm focus:ring-[#C86D51]"
                    data-testid="lead-form-phone-input"
                  />
                </div>
              </div>

              {/* MCQ Question */}
              <div className="space-y-2">
                <span className="block text-xs font-semibold uppercase tracking-wider text-[#4A423E]">What challenges are you currently facing?</span>
                <div className="grid grid-cols-1 gap-2.5">
                  <label 
                    className={`border p-3.5 rounded-xl cursor-pointer flex items-start space-x-3 transition-custom text-left ${
                      formProblem === "Postpartum" 
                        ? "border-[#C86D51] bg-[#F8ECE8]/50 text-[#C86D51]" 
                        : "border-[#E4DDD3] bg-transparent text-[#4A423E] hover:bg-stone-50"
                    }`}
                    data-testid="lead-form-mcq-postpartum-label"
                  >
                    <input 
                      type="radio"
                      name="modal-challenge-category"
                      value="Postpartum"
                      checked={formProblem === "Postpartum"}
                      onChange={() => setFormProblem("Postpartum")}
                      className="mt-1 accent-[#C86D51] shrink-0"
                      data-testid="lead-form-mcq-postpartum-radio"
                    />
                    <div>
                      <span className="block font-semibold text-xs sm:text-sm">Postpartum Difficulties</span>
                      <span className="block text-[10px] sm:text-xs opacity-80 leading-relaxed mt-0.5">Includes acute anxiety, panic attacks, clinical exhaustion, postpartum relationship tension and identity mourning.</span>
                    </div>
                  </label>

                  <label 
                    className={`border p-3.5 rounded-xl cursor-pointer flex items-start space-x-3 transition-custom text-left ${
                      formProblem === "Others" 
                        ? "border-[#C86D51] bg-[#F8ECE8]/50 text-[#C86D51]" 
                        : "border-[#E4DDD3] bg-transparent text-[#4A423E] hover:bg-stone-50"
                    }`}
                    data-testid="lead-form-mcq-others-label"
                  >
                    <input 
                      type="radio"
                      name="modal-challenge-category"
                      value="Others"
                      checked={formProblem === "Others"}
                      onChange={() => setFormProblem("Others")}
                      className="mt-1 accent-[#C86D51] shrink-0"
                      data-testid="lead-form-mcq-others-radio"
                    />
                    <div>
                      <span className="block font-semibold text-xs sm:text-sm">Others (Explain below)</span>
                      <span className="block text-[10px] sm:text-xs opacity-80 leading-relaxed mt-0.5">For general relationship counseling, family disagreements or other specific struggles.</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-1">
                <label htmlFor="modal-details" className="block text-xs font-semibold uppercase tracking-wider text-[#4A423E]">Elaborate or Describe your Situation (Optional)</label>
                <Textarea 
                  id="modal-details"
                  placeholder="If comfortable, share a few words about what you are currently going through. This helps Aparnaa prepare before calling."
                  value={formDetails}
                  onChange={(e) => setFormDetails(e.target.value)}
                  className="border-[#E4DDD3] focus:border-[#C86D51] text-sm min-h-[80px]"
                  data-testid="lead-form-details-input"
                />
              </div>

              {/* Submit */}
              <div className="pt-2">
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#C86D51] hover:bg-[#A85339] text-white text-base font-semibold py-6 rounded-xl shadow transition-custom flex items-center justify-center space-x-2"
                  data-testid="lead-form-submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      <span>Saving Secure Booking...</span>
                    </>
                  ) : (
                    <>
                      <span>Submit Secure Booking Request</span>
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </Button>
              </div>
            </form>

            <p className="text-center text-[11px] text-[#8C827A] italic leading-relaxed pt-1">
              Your callback booking request is fully secure and handled under absolute professional confidentiality.
            </p>
          </div>
        </div>
      )}

    </div>
  );
}
